
import ASSETS from '@generio-ai/library-legacy/constants/assets'
import { File } from '@generio-ai/library-legacy/stores/_api'
import { requires } from '@/utils/validate.js'


export default {

    // intermediate: post assets
    async intermediatePostAssets({ getters, dispatch }, {
        app,
        card,
        parentCard = null,
        fileData = null,
        fileParameters = null,
        ignore = false,
        preload = false
    } = {}) {
        // verify app is registered
        if (!getters.isRegistered(app)) {
            throw new Error(`App '${app}' is not registered in flows store.`)
        }

        // ensure parameters are present
        requires(card, fileData)

        try {
            // placeholder for imported assets
            const importedAssets = []

            // prepare additional
            const additional = card.createAdditional(parentCard, ignore)

            // ensure fileData is an array
            const fileDataArray = Array.isArray(fileData) ? fileData : [fileData]
            const fileParametersArray = Array.isArray(fileParameters) ? fileParameters : fileDataArray.map(() => fileParameters)

            // TODO check for type (image or model)

            // iterate through all fileData
            for (let index = 0; index < fileDataArray.length; index++) {
                const fileDataEntry = fileDataArray[index]
                const fileParametersEntry = fileParametersArray[index] || null

                // prepare file
                const file = new File(ASSETS.FILES.KEY.DEFAULT, fileDataEntry, {}, fileParametersEntry)

                // create asset
                const response = await dispatch('assets/create', {
                    app,
                    files: [file],
                    thumbnail: true,
                    additional
                }, { root: true })

                // add asset to list
                importedAssets.push(response.assets[0])
            }

            // if assets should be preloaded
            if (preload) {
                await dispatch("awaitRetrieved", {
                    assets: importedAssets
                })
            }

            // resolved successfully
            return { assets: importedAssets }
        }
        catch (error) {
            throw error
        }
    },

    async intermediateCloneAssets({ getters, dispatch }, {
        app,
        card,
        parentCard = null,
        assets = [],
        fileKeys = [ ASSETS.FILES.KEY.DEFAULT ],
        ignore = false,
        preload = false
    } = {}) {
        // verify app is registered
        if (!getters.isRegistered(app)) {
            throw new Error(`App '${app}' is not registered in flows store.`)
        }

        // ensure parameters are present
        requires(card, assets, fileKeys)

        try {
            // placeholder for cloned assets
            const clonedAssets = []

            // prepare additional
            const additional = card.createAdditional(parentCard, ignore)

            for (const asset of assets) {
                // call route to clone asset
                const response = await dispatch('assets/clone', {
                    app,
                    id: asset.id,
                    fileKeys,
                    additional
                }, { root: true })

                // add cloned asset to list
                clonedAssets.push(response.assets[0])
            }

            // if assets should be preloaded
            if (preload) {
                await dispatch("awaitRetrieved", {
                    assets: clonedAssets
                })
            }

            // resolved successfully
            return { assets: clonedAssets }
        }
        catch (error) {
            throw error
        }
    },

    // intermediate: post file
    async intermediatePostFile({ dispatch }, {
        asset = null,
        fileData = null,
        fileKey = ASSETS.FILES.KEY.DEFAULT,
        overwrite = true,
        preload = false
    } = {}) {
        // ensure parameters are present
        requires(asset, fileData, fileKey)

        try {
            // create file in existing asset
            await dispatch('assets/createFile', {
                id: asset.id,
                fileKey: fileKey,
                fileData: fileData,
                overwrite: overwrite
            }, { root: true })

            // if asset should be preloaded
            if (preload) {
                await dispatch("awaitRetrieved", {
                    assets: [asset],
                    fileKey
                })
            }

            // resolved successfully
            return { assets: [asset] }
        }
        catch (error) {
            throw error
        }
    },

    // intermediate: remove background
    async intermediateRemoveBackground({ getters, dispatch }, {
        app,
        card,
        parentCard = null,
        asset = null,
        fileKey = ASSETS.FILES.KEY.DEFAULT,
        mask = true,
        ignore = false,
        preload = false
    } = {}) {
        // verify app is registered
        if (!getters.isRegistered(app)) {
            throw new Error(`App '${app}' is not registered in flows store.`)
        }

        // ensure parameters are present
        requires(card, asset)

        try {
            // placeholder for image assets
            let imageAssets = null

            // prepare additional
            const additional = card.createAdditional(parentCard, ignore)

            // wait for all assets to be ready
            await dispatch("awaitReady", {
                assets: [asset],
                fileKey
            })

            // call route to remove background
            const response = await dispatch('assets/segmentImage', {
                app,
                id: asset.id,
                fileKey,
                mask,
                additional
            }, { root: true })

            // retrieve image assets from response
            imageAssets = response.assets

            // if asset should be preloaded
            if (preload) {
                await dispatch("awaitRetrieved", {
                    assets: imageAssets
                })
            }

            // resolved successfully
            return { assets: imageAssets }
        }
        catch (error) {
            throw error
        }
    },

    // intermediate: generate text
    async intermediateGenerateText({ getters, dispatch }, {
        app,
        card,
        parentCard = null,
        count = 1,
        prompt = null,
        template = null,
        ignore = false,
        preload = false
    } = {}) {
        // verify app is registered
        if (!getters.isRegistered(app)) {
            throw new Error(`App '${app}' is not registered in flows store.`)
        }

        // ensure parameters are present
        requires(card, prompt, template)

        // ensure template is defined
        if (!['enhance_prompt', 'translate_prompt'].includes(template)) {
            throw new Error(`Invalid template: ${template}`)
        }

        try {
            // placeholder for text assets
            let textAssets = null

            // prepare parameters
            const parameters = {
                text_variants: count
            }

            // prepare additional
            const additional = card.createAdditional(parentCard, ignore)

            // call route to generate text
            const response = await dispatch('assets/generateTextFromPrompt', {
                app,
                prompt,
                template,
                parameters,
                additional
            }, { root: true })

            // collect asset from response
            textAssets = [response.assets[0]]

            // retrieve asset
            if (preload) {
                await dispatch('awaitRetrieved', {
                    assets: textAssets
                })
            }

            // resolved successfully
            return { assets: textAssets }
        }
        catch (error) {
            throw error
        }
    }
}
